import {useMutation, useQuery, useQueryClient} from '@tanstack/react-query'

import {toBusinessApiDateString} from 'src/jobs/job-schedule-format'
import {jobKeys} from 'src/jobs/query-keys'
import {invalidateProjectBillableLineItemsQueries} from 'src/projects/invalidate-project-billable-line-items'
import {projectKeys} from 'src/projects/query-keys'

import {
    createProjectRentalBillingCharges,
    createRentalBillingCharges,
    getProjectRentalBillingPreview,
    getRentalBillingPreview,
} from './api'
import {rentalBillingKeys} from './query-keys'
import type {RentalBillingChargeCreateInput} from './schemas'

export function useRentalBillingPreview(
    jobId: string,
    billThrough: Date | string | null,
    enabled = true,
) {
    return useQuery({
        queryKey: rentalBillingKeys.preview(
            jobId,
            billThrough ? toBusinessApiDateString(billThrough) : 'not-selected',
        ),
        queryFn: () => getRentalBillingPreview(jobId, billThrough!),
        enabled: enabled && billThrough != null,
    })
}

export function useProjectRentalBillingPreview(
    projectId: string,
    billThrough: Date | string | null,
    rentalStintIds: string[] | undefined,
    enabled = true,
) {
    const sortedStintIds = [...(rentalStintIds ?? [])].sort()
    return useQuery({
        queryKey: rentalBillingKeys.projectPreview(
            projectId,
            billThrough ? toBusinessApiDateString(billThrough) : 'not-selected',
            sortedStintIds,
        ),
        queryFn: () => getProjectRentalBillingPreview(projectId, billThrough!, rentalStintIds),
        enabled: enabled && billThrough != null,
    })
}

export function useCreateRentalBillingCharges(jobId: string, projectId: string) {
    const queryClient = useQueryClient()

    return useMutation({
        mutationFn: (input: RentalBillingChargeCreateInput) =>
            createRentalBillingCharges(jobId, input),
        onSuccess: async () => {
            await Promise.all([
                queryClient.invalidateQueries({queryKey: rentalBillingKeys.all}),
                queryClient.invalidateQueries({queryKey: jobKeys.detail(jobId)}),
                invalidateProjectBillableLineItemsQueries(queryClient, projectId),
            ])
        },
    })
}

export function useCreateProjectRentalBillingCharges(projectId: string) {
    const queryClient = useQueryClient()

    return useMutation({
        mutationFn: (input: RentalBillingChargeCreateInput) =>
            createProjectRentalBillingCharges(projectId, input),
        onSuccess: async () => {
            await Promise.all([
                queryClient.invalidateQueries({queryKey: rentalBillingKeys.all}),
                invalidateProjectBillableLineItemsQueries(queryClient, projectId),
                queryClient.invalidateQueries({queryKey: projectKeys.detail(projectId)}),
                queryClient.invalidateQueries({queryKey: jobKeys.all}),
            ])
        },
    })
}
